import { ReactNode } from 'react'
import { Navigate } from 'react-router-dom'
import { useAuth } from '@/services/auth'
import { useShouldShowAdmin } from '@/utils/admin'

interface AdminRouteProps {
  children: ReactNode
}

/**
 * Route guard for admin-only pages
 *
 * Production: Redirects to home if user is not an admin.
 * Development: Allows any authenticated user (dev mode bypass).
 * Shows loading state while checking authentication.
 *
 * Related: Issue #280, Issue #294, specs/037-admin-permissions/spec.md § Development Environment Behavior
 *
 * @example
 * <Route path="/admin/users" element={
 *   <AdminRoute>
 *     <AdminUsersPage />
 *   </AdminRoute>
 * } />
 */
export function AdminRoute({ children }: AdminRouteProps) {
  const { user, isLoading } = useAuth()
  const shouldShowAdmin = useShouldShowAdmin()

  // Show loading state while checking auth
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          <p className="mt-2 text-sm text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  // Must be logged in and meet admin visibility criteria
  if (user && shouldShowAdmin) {
    // Render admin content
    return <>{children}</>
  }

  return <Navigate to="/" replace />
}
